export type TimeZoneOption = { value: string; label: string };

/** The IANA zone the browser or device runs in, or undefined when Intl cannot tell. */
export function getDeviceTimeZone(): string | undefined {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
	} catch {
		return undefined;
	}
}

function offsetOf(zone: string, at: Date): { minutes: number; label: string } {
	const name =
		new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' })
			.formatToParts(at)
			.find(part => part.type === 'timeZoneName')?.value ?? 'GMT';
	const match = /GMT([+-])(\d{2}):(\d{2})/.exec(name);
	if (!match) {
		return { minutes: 0, label: 'UTC+00:00' };
	}

	const [, sign, hours, minutes] = match;
	return {
		minutes: (sign === '-' ? -1 : 1) * (Number(hours) * 60 + Number(minutes)),
		label: `UTC${sign}${hours}:${minutes}`,
	};
}

/**
 * Lists every IANA zone the runtime supports, plus `include` values it leaves out (`UTC`, and
 * aliases such as `Asia/Calcutta` that a browser can still report). Labels show the current UTC
 * offset, and the list is sorted by that offset.
 */
export function getTimeZoneOptions(include: (string | null | undefined)[] = [], at = new Date()): TimeZoneOption[] {
	const zones = new Set([...Intl.supportedValuesOf('timeZone'), 'UTC']);
	for (const zone of include) {
		if (zone) {
			zones.add(zone);
		}
	}

	return [...zones]
		.map(zone => {
			const offset = offsetOf(zone, at);
			return { zone, offset, label: `(${offset.label}) ${zone.replaceAll('_', ' ')}` };
		})
		.sort((a, b) => a.offset.minutes - b.offset.minutes || a.zone.localeCompare(b.zone))
		.map(({ zone, label }) => ({ value: zone, label }));
}
